import {
  Box,
  Container,
  Divider,
  Grid,
  Typography,
  CircularProgress,
  Button,
} from "@mui/material";
import { lightBlue } from "@mui/material/colors";
import React from "react";
import { db } from "../../Environment/firebase";
import { useAuthContext } from "../../Modules/Auth/AuthContext";
import CaseCard, { CaseCardType } from "../../Modules/ViewCase/CaseCard";
import { Timestamp } from "firebase/firestore";
import { MstaFontFamily } from "../../Functions/Style/MstaFont";

interface DataType {
  id: string;
  registrationDate: Timestamp;
}

const PAGE_SIZE = 20;

function Good() {
  const { user } = useAuthContext();
  const [datas, setDatas] = React.useState<DataType[]>([]);
  const [cases, setCases] = React.useState<CaseCardType[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [lastDoc, setLastDoc] = React.useState<Timestamp | null>(null);
  const [hasMore, setHasMore] = React.useState(true);

  React.useEffect(() => {
    if (user?.uid) {
      fetchGoodCases();
    }
  }, [user?.uid]);

  const fetchGoodCases = async (startAfter: Timestamp | null = null) => {
    setLoading(true);

    try {
      let query = db
        .collection("users")
        .doc(user?.uid)
        .collection("good")
        .orderBy("registrationDate", "desc")
        .limit(PAGE_SIZE);

      if (startAfter) {
        query = query.startAfter(startAfter);
      }

      const querySnapshot = await query.get();
      const newDatas: DataType[] = [];

      querySnapshot.forEach((doc) => {
        const docData = doc.data();
        newDatas.push({
          id: doc.id,
          registrationDate: docData.registrationDate,
        });
      });

      if (newDatas.length < PAGE_SIZE) {
        setHasMore(false);
      }

      setDatas((prev) => [...prev, ...newDatas]);
      setLastDoc(newDatas[newDatas.length - 1]?.registrationDate || null);

      const ids = newDatas.map((item) => item.id);
      const dwhData = await getDWHData(ids);

      setCases((prev) => [...prev, ...dwhData]);
    } finally {
      setLoading(false);
    }
  };

  const getDWHData = async (ids: string[]) => {
    const dwhData: CaseCardType[] = [];
    for (const id of ids) {
      const doc = await db.collection("dwh").doc(id).get();
      if (doc.exists) {
        const docData = doc.data();
        const formattedData: CaseCardType = {
          id: doc.id,
          title: docData?.title,
          age: docData?.ageSearch,
          sex: docData?.sex,
          patientId: docData?.patientId,
          site: docData?.site,
          modality: docData?.modality,
          date: docData?.date,
          organTags: docData?.organTags,
          keyImages: docData?.KeyImages,
          casenumber: docData?.casenumber,
        };
        dwhData.push(formattedData);
      }
    }
    return dwhData;
  };

  const loadMore = () => {
    if (lastDoc) {
      fetchGoodCases(lastDoc);
    }
  };

  return (
    <Container sx={{ py: 1, mb: 5, minHeight: "95vh" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          m: 1,
          mt: 3,
          gap: 1.5,
        }}
      >
        <Box sx={{ width: 20, height: 44, bgcolor: lightBlue[400] }} />
        <Typography variant="h4" alignSelf="center" fontFamily={MstaFontFamily}>
          いいね
        </Typography>
      </Box>
      <Divider
        sx={{
          borderBottomWidth: 1.5,
          borderColor: "black",
          mx: 1,
          mt: 1,
          mb: 4,
        }}
      />
      {loading && datas.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            minHeight: "50vh",
          }}
        >
          <CircularProgress />
        </Box>
      ) : datas.length === 0 && !loading ? (
        <div className="table">No rows to show</div>
      ) : (
        <Grid container sx={{ px: 1 }} spacing={4}>
          {cases.map((caseItem) => (
            <Grid
              item
              xs={12}
              sm={6}
              key={caseItem.id}
              sx={{
                maxWidth: "100%",
              }}
            >
              <CaseCard
                id={caseItem.id}
                title={caseItem.title}
                age={caseItem.age}
                sex={caseItem.sex}
                patientId={caseItem.patientId}
                site={caseItem.site}
                modality={caseItem.modality}
                date={caseItem.date}
                organTags={caseItem.organTags}
                keyImages={caseItem.keyImages}
                casenumber={caseItem.casenumber}
              />
            </Grid>
          ))}
        </Grid>
      )}
      {hasMore && !loading && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <Button
            onClick={loadMore}
            disabled={loading}
            sx={{ textTransform: "none" }}
          >
            Load More
          </Button>
        </Box>
      )}
      {loading && datas.length > 0 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      )}
    </Container>
  );
}

export default Good;
